/** Runtime configuration, fetched from /config.json before the app boots.
 *  Keeping it out of the bundle means one build can be deployed to any
 *  environment — a future S3/CloudFront deploy just writes a different file. */
export interface AppConfig {
  /** HTTP API endpoint, no trailing slash, e.g. https://abc123.execute-api.eu-central-1.amazonaws.com */
  apiBaseUrl: string;
  /** Cognito hosted-UI domain, e.g. ai-workbench.auth.eu-central-1.amazoncognito.com */
  cognitoDomain: string;
  cognitoClientId: string;
  /** OAuth scopes requested at sign-in. */
  scopes: string;
  /** Shows the Administration section. Kept as a flag so it can be hidden
   *  once the backend distinguishes admins from ordinary users. */
  adminEnabled: boolean;
}

const DEFAULTS = {
  scopes: 'openid email profile',
  adminEnabled: true,
};

function requireString(source: Record<string, unknown>, key: string): string {
  const value = source[key];
  if (typeof value !== 'string' || value.trim() === '') {
    throw new Error(`config.json is missing the "${key}" setting`);
  }
  return value.trim();
}

export async function loadConfig(): Promise<AppConfig> {
  const response = await fetch(`${import.meta.env.BASE_URL}config.json`, { cache: 'no-store' });
  if (!response.ok) {
    throw new Error(`Could not load config.json (HTTP ${response.status})`);
  }
  const raw = (await response.json()) as Record<string, unknown>;
  return {
    apiBaseUrl: requireString(raw, 'apiBaseUrl').replace(/\/+$/, ''),
    cognitoDomain: requireString(raw, 'cognitoDomain').replace(/^https?:\/\//, '').replace(/\/+$/, ''),
    cognitoClientId: requireString(raw, 'cognitoClientId'),
    scopes: typeof raw.scopes === 'string' ? raw.scopes : DEFAULTS.scopes,
    adminEnabled: typeof raw.adminEnabled === 'boolean' ? raw.adminEnabled : DEFAULTS.adminEnabled,
  };
}
